/* eslint-disable @typescript-eslint/no-explicit-any */
import { serialize } from 'cookie';
import { cloneDeep, get, set } from 'lodash';
import type { NextApiRequest, NextApiResponse } from 'next';

import { appInstance } from '../../../config/constants';
import { getLocaleFromUrl } from '../../../../common/services/instance.service';
import { getSession } from '../../services/session.service';
import {
  urlParse
} from '../../../../common/utils/url.util';
import {
  getFlowAndPartnerByPath,
  getPageNameAndVerticalByPath,
  getUrl,
} from '../../../../common/utils/path-mapper.util';
import { Config, Vertical, Session } from '@bolttech/essentials';

// export const config = {
//   api: {
//     externalResolver: true,
//   },
// };

/*  TODO: urlMap on frontend-shared was ref by @client(country-instance)
let's keep it as a param any and thing a way to change it in the future */

export async function getUtmUrl(
  req: NextApiRequest,
  res: NextApiResponse,
  session: Session,
  config: Config,
  url: string,
  options?: { partnersWithoutHomepage: string[] },
  query?: Record<string, unknown>,
  utmsDefault?: Record<string, unknown>,
  urlMap?: any,
): Promise<string> {
  let utms =
    get(session, 'utm') ||
    get(req.headers.cookie, 'utms') ||
    cloneDeep(utmsDefault);

  try {
    if (typeof utms === 'string') utms = JSON.parse(utms);
  } catch (e) {
    console.warn('Unable to parse UTMS');
  }

  const { pageName, vertical, locale, flow } = config;
  const utmFromSession = utms[vertical as string]?.source || 'Direct';
  const utmSourceFromUrl =
    get(query, 'utm_source') || get(req, 'query.utm_source');
  const utmMediumFromUrl =
    get(query, 'utm_medium') || get(req, 'query.utm_medium');
  const utmCampaignFromUrl =
    get(query, 'utm_campaign') || get(req, 'query.utm_campaign');
  const utmContentFromUrl =
    get(query, 'utm_content') || get(req, 'query.utm_content');
  const utmTermFromUrl = get(query, 'utm_term') || get(req, 'query.utm_term');
  const utmTransactionFromUrl =
    get(query, 'utm_transaction') || get(req, 'query.utm_transaction');
  const utmCustomerFromUrl =
    get(query, 'utm_customer') || get(req, 'query.utm_customer');

  const quoteHFromUrl = get(req, 'query.h');
  const quoteXFromUrl = get(req, 'query.x');

  // Update UTM params
  if (utmMediumFromUrl && typeof utmMediumFromUrl === 'string')
    Object.keys(utms).forEach((key: string) =>
      set(utms, `${key}.medium`, encodeURIComponent(utmMediumFromUrl)),
    );
  if (utmCampaignFromUrl && typeof utmCampaignFromUrl === 'string')
    Object.keys(utms).forEach((key: string) =>
      set(utms, `${key}.campaign`, encodeURIComponent(utmCampaignFromUrl)),
    );
  if (utmContentFromUrl && typeof utmContentFromUrl === 'string')
    Object.keys(utms).forEach((key: string) =>
      set(utms, `${key}.content`, encodeURIComponent(utmContentFromUrl)),
    );
  if (utmTermFromUrl && typeof utmTermFromUrl === 'string')
    Object.keys(utms).forEach((key: string) =>
      set(utms, `${key}.term`, encodeURIComponent(utmTermFromUrl)),
    );
  if (utmTransactionFromUrl && typeof utmTransactionFromUrl === 'string')
    Object.keys(utms).forEach((key: string) =>
      set(
        utms,
        `${key}.transaction`,
        encodeURIComponent(utmTransactionFromUrl),
      ),
    );
  if (utmCustomerFromUrl && typeof utmCustomerFromUrl === 'string')
    Object.keys(utms).forEach((key: string) =>
      set(
        utms,
        `${key}.customer`,
        encodeURIComponent(utmCustomerFromUrl),
      ),
    );

  let utmSource =
    !!utmSourceFromUrl && utmSourceFromUrl !== utmFromSession
      ? utmSourceFromUrl
      : utmFromSession;
  if (
    options?.partnersWithoutHomepage &&
    options?.partnersWithoutHomepage.includes(utmFromSession)
  ) {
    utmSource = utmFromSession;
  }

  const redirectUrl = getUrl(
    pageName as string,
    vertical as Vertical,
    locale as string,
    flow,
    utmSource,
    {},
    req.headers.origin as string,
    urlMap
  );
  const redirectFlowDetails = getFlowAndPartnerByPath(redirectUrl, urlMap);
  const { partner } = (redirectFlowDetails as Record<string, string>) || {};

  if (utmSource !== 'Direct') {
    Object.keys(utms).forEach((key: string) => {
      set(utms, `${key}.source`, encodeURIComponent(utmSource));
    });
  }

  const utmsFromUrl: Record<string, string> = {
    utm_source: utmSourceFromUrl as string,
    utm_medium: utmMediumFromUrl as string,
    utm_campaign: utmCampaignFromUrl as string,
    utm_content: utmContentFromUrl as string,
    utm_term: utmTermFromUrl as string,
    utm_transaction: utmTransactionFromUrl as string,
    utm_customer: utmCustomerFromUrl as string,
  };
  res.setHeader('Set-Cookie', [
    serialize('utms', JSON.stringify(utms), {
      encode: (value) => value,
      httpOnly: true,
      sameSite: 'strict',
      path: '/',
    }),
    serialize('utmsFromUrl', JSON.stringify(utmsFromUrl), {
      encode: (value) => value,
      httpOnly: false,
      sameSite: 'lax',
      path: '/',
    }),
  ]);

  set(session, 'utm', utms);
  set(session, 'utms', utms);
  set(session, 'utmsFromUrl', utmsFromUrl);
  set(session, 'locale', locale);
  set(session, 'partner.name', partner);
  set(session, 'quoteHash.h', quoteHFromUrl);
  set(session, 'quoteHash.x', quoteXFromUrl);
  if (partner === 'Direct') set(session, 'partner', null);

  session.commit();

  if (partner === utmSource && utmSource !== 'Direct') {
    if (partner) {
      return redirectUrl;
    }
  }

  return url;
}

async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  urlMap?: any,
): Promise<NextApiResponse> {
  const session = await getSession(req, res);

  const parsedUrl = urlParse(
    decodeURIComponent(req.query?.url as string).replace(
      /\/_next\/data\/development(.+)\.json.+/,
      '$1',
    ),
  );

  const { pathname } = parsedUrl;
  const url = decodeURIComponent(pathname as string);
  const locale = getLocaleFromUrl(url, appInstance);
  const { pageName, vertical } = getPageNameAndVerticalByPath(url, urlMap);
  const flowDetails = getFlowAndPartnerByPath(url, urlMap);
  const { flow } = (flowDetails as Record<string, string>) || {};

  const redirectUrl = await getUtmUrl(
    req,
    res,
    session,
    {
      pageName,
      vertical,
      locale,
      flow,
    },
    url,
  );

  return res.redirect(redirectUrl);
}

export default handler;
